import useSWR, { mutate as globalMutate } from 'swr';
import { useState, useCallback } from 'react';
import { mailboxService, type Conversation as _Conversation, type Message as _Message, type SendReplyRequest } from '@/lib/services/mailbox';
import { toast } from 'sonner';

export function useConversations(userID: string, search?: string, isCampaign?: boolean, refreshInterval = 30000) {
  const { data, error, isLoading, mutate } = useSWR(
    userID ? [`conversations`, userID, search, isCampaign] : null,
    () => mailboxService.getConversations(userID, search, undefined, undefined, isCampaign),
    {
      refreshInterval,
      revalidateOnFocus: true,
    }
  );

  const deleteConversation = useCallback(async (conversationID: string) => {
    try {
      await mailboxService.deleteConversation(userID, conversationID);
      toast.success('Conversation deleted');
      mutate();
    } catch (error) {
      toast.error('Failed to delete conversation');
      console.error('Error deleting conversation:', error);
    }
  }, [userID, mutate]);

  const markAsRead = useCallback(async (conversationID: string) => {
    try {
      await mailboxService.markConversationAsRead(userID, conversationID);
      // Force revalidation to get fresh data from server
      await mutate(undefined, { revalidate: true });
      // Also invalidate all conversation caches for this user to update sidebar count
      // This matches the cache key pattern used by useConversations
      await globalMutate(
        (key) => Array.isArray(key) && key[0] === 'conversations' && key[1] === userID,
        undefined,
        { revalidate: true }
      );
    } catch (error) {
      console.error('Error marking conversation as read:', error);
      // Still revalidate on error to ensure UI is in sync
      mutate();
      globalMutate(
        (key) => Array.isArray(key) && key[0] === 'conversations' && key[1] === userID,
        undefined,
        { revalidate: true }
      );
    }
  }, [userID, mutate]);

  return {
    conversations: data?.conversations || [],
    total: data?.total || 0,
    isLoading,
    error,
    deleteConversation,
    markAsRead,
    refresh: mutate,
  };
}

export function useConversation(userID: string, conversationID: string | null) {
  const { data, error, isLoading } = useSWR(
    userID && conversationID ? [`conversation`, userID, conversationID] : null,
    () => mailboxService.getConversation(userID, conversationID!),
    {
      refreshInterval: 30000,
    }
  );

  return {
    conversation: data,
    isLoading,
    error,
  };
}

export function useMessages(userID: string, conversationID: string | null) {
  const { data, error, isLoading, mutate } = useSWR(
    userID && conversationID ? [`messages`, userID, conversationID] : null,
    () => mailboxService.getMessages(userID, conversationID!),
    {
      refreshInterval: 10000,
      revalidateOnFocus: true,
    }
  );

  const sendReply = useCallback(async (reply: SendReplyRequest) => {
    if (!conversationID) return;
    
    try {
      const result = await mailboxService.sendReply(conversationID, reply);
      toast.success('Reply sent');
      mutate();
      return result;
    } catch (error) {
      toast.error('Failed to send reply');
      console.error('Error sending reply:', error);
      throw error;
    }
  }, [conversationID, mutate]);

  const markMessageAsRead = useCallback(async (messageID: string) => {
    try {
      await mailboxService.markMessageAsRead(userID, messageID);
      mutate();
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  }, [userID, mutate]);

  return {
    messages: data?.messages || [],
    total: data?.total || 0,
    isLoading,
    error,
    sendReply,
    markMessageAsRead,
    refresh: mutate,
  };
}

export function useMailboxSearch(userID: string) {
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const { data, error, isLoading } = useSWR(
    userID && query ? [`search`, userID, query] : null,
    () => mailboxService.search(userID, query),
    {
      revalidateOnFocus: false,
      dedupingInterval: 1000,
    }
  );

  const search = useCallback(async (searchQuery: string) => {
    setQuery(searchQuery);
    setIsSearching(true);
    
    try {
      setTimeout(() => setIsSearching(false), 500);
    } catch (error) {
      setIsSearching(false);
      toast.error('Search failed');
    }
  }, []);

  const clearSearch = useCallback(() => {
    setQuery('');
    setIsSearching(false);
  }, []);

  return {
    query,
    results: data,
    isLoading: isLoading || isSearching,
    error,
    search,
    clearSearch,
  };
}

